import { Alert, AlertType } from '@design/components/alert/Alert';
import { useRunScheduledQueryNow } from '@terminal/react-query-hooks/alfred/useScheduledQueryMutations';
import { useUserPhone } from '@terminal/react-query-hooks/user-phone/useUserPhone';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { useSubscribeToRunResult } from '@terminal/views/alfred/research/hooks/useSubscribeToRunResult';
import { useTranslation } from 'react-i18next';

export type ScheduledQueryToastVariant = 'created' | 'updated';

const PHONE_REFETCH_TIMEOUT_MS = 2000;

const SCHEDULED_TOAST_KEYS: Record<
  ScheduledQueryToastVariant,
  { title: string; message: string; whatsappMessage: string }
> = {
  created: {
    title: 'toastTaskScheduledTitle',
    message: 'toastTaskScheduledMessage',
    whatsappMessage: 'toastTaskScheduledMessageWhatsApp',
  },
  updated: {
    title: 'toastTaskUpdatedTitle',
    message: 'toastTaskUpdatedMessage',
    whatsappMessage: 'toastTaskUpdatedMessageWhatsApp',
  },
};

export const useScheduledQueryToast = () => {
  const { t } = useTranslation(['chat']);
  const { showToast, removeToast } = useToasts(state => ({
    showToast: state.showToast,
    removeToast: state.removeToast,
  }));
  const { mutate: runNow } = useRunScheduledQueryNow();
  const { subscribe } = useSubscribeToRunResult();
  const { trackScheduledQueryDrawer } = usePosthogEvents();
  const isFeatureEnabled = useFlags(state =>
    state.getFeatureFlagState('alfred-whatsapp-integration')
  );
  const isTasksUpdate = useFlags(state =>
    state.getFeatureFlagState('REASON-2703-scheduled-tasks-update')
  );
  const { data: phoneStatus, refetch: refetchPhoneStatus } =
    useUserPhone(isFeatureEnabled);

  const showAlertToast = (type: AlertType, title: string, message: string) => {
    const toastId = `scheduled-query-toast-${Date.now()}`;
    showToast({
      id: toastId,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          type={type}
          title={title}
          message={message}
          onClose={() => removeToast(toastId)}
        />
      ),
    });
  };

  const showScheduledToast = async (
    taskId: string,
    variant: ScheduledQueryToastVariant
  ) => {
    const {
      title: titleKey,
      message: messageKey,
      whatsappMessage: whatsappMessageKey,
    } = SCHEDULED_TOAST_KEYS[variant];
    const toastId = `task-toast-${Date.now()}`;

    const latestPhoneStatus = isFeatureEnabled
      ? await Promise.race([
          refetchPhoneStatus().then(result =>
            result.isError ? phoneStatus : result.data
          ),
          new Promise<typeof phoneStatus>(resolve =>
            setTimeout(() => resolve(phoneStatus), PHONE_REFETCH_TIMEOUT_MS)
          ),
        ]).catch(() => phoneStatus)
      : undefined;
    const isWhatsAppVerified =
      isFeatureEnabled && latestPhoneStatus?.verified === true;

    showToast({
      id: toastId,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          type={AlertType.Success}
          title={t(`chat:scheduledQuerySettings.${titleKey}`)}
          message={t(
            `chat:scheduledQuerySettings.${
              isWhatsAppVerified ? whatsappMessageKey : messageKey
            }`,
            {
              context: isTasksUpdate ? 'task' : undefined,
              ...(isWhatsAppVerified && {
                phone: latestPhoneStatus.phone_masked,
              }),
            }
          )}
          onClose={() => removeToast(toastId)}
          primaryBtn={{
            label: t('chat:scheduledQuerySettings.runNow'),
            onClick: () => {
              removeToast(toastId);
              trackScheduledQueryDrawer({
                action: 'run',
                source: POSTHOG_TRACKING_SOURCES.SCHEDULED_TOAST,
                promptId: taskId,
              });
              const unsubscribe = subscribe(taskId);
              runNow(taskId, { onError: () => unsubscribe() });
            },
          }}
        />
      ),
    });
  };

  const showPromptDeletedToast = () => {
    showAlertToast(
      AlertType.Success,
      t('chat:promptLibrary.deletePromptSuccessTitle'),
      t('chat:promptLibrary.deletePromptSuccessMessage')
    );
  };

  const showPromptDeleteFailedToast = () => {
    showAlertToast(
      AlertType.Error,
      t('chat:promptLibrary.deletePromptFailedTitle'),
      t('chat:promptLibrary.deletePromptFailedMessage')
    );
  };

  return {
    showScheduledToast,
    showPromptDeletedToast,
    showPromptDeleteFailedToast,
  };
};
